import { useState, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  Image,
  Icon,
  Textarea,
  Checkbox,
  CircularProgress,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IoIosArrowBack, IoMdClose } from "react-icons/io";
import { FaRegCircleCheck, FaStar, FaRegStar } from "react-icons/fa6";
import { IoCameraOutline } from "react-icons/io5";

import { reviewRating } from "../services/booking";
import { getCookie } from "../core/utils";
import { OpenInAppBanner } from "../components/OpenInAppBanner";
import LogoIcon from "../assets/images/logoIcon.png";
import LogoText from "../assets/images/logoText.png";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
const isAndroid = /Android/.test(navigator.userAgent);
const isMobile = isIOS || isAndroid;

export const ReviewBooking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const jwt = getCookie("book-d booking token");

  const businessId = searchParams.get("business_id");
  const bookingDetailsId = searchParams.get("booking_details_id");
  const serviceId = searchParams.get("service_id");
  const targetUserId = searchParams.get("target_user_id");

  const queryString = searchParams.toString();
  const currentPath = queryString
    ? `/review-booking?${queryString}`
    : "/review-booking";

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [isAnonymous, setIsAnonymous] = useState(false);

  // iOS Smart App Banner — shows a native prompt in Safari, no JS redirect
  useEffect(() => {
    if (!isIOS || !bookingDetailsId) return;
    const meta = document.createElement("meta");
    meta.name = "apple-itunes-app";
    meta.content = `app-id=6558535790, app-argument=bookd://booking-details/${bookingDetailsId}?openReviewSheet=true`;
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, [bookingDetailsId]);

  useEffect(() => {
    if (!jwt) {
      navigate("/login", {
        state: { returnTo: currentPath },
        replace: true,
      });
    }
  }, [jwt, currentPath, navigate]);

  const handleStarClick = (starIndex: number) => {
    setRating(starIndex);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos = Array.from(files);
    const totalPhotos = photos.length + newPhotos.length;

    if (totalPhotos > 4) {
      toast({
        title: "Too many photos",
        description: "You can only upload up to 4 photos.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      const allowedNewPhotos = newPhotos.slice(0, 4 - photos.length);
      setPhotos((prev) => [...prev, ...allowedNewPhotos]);
    } else {
      setPhotos((prev) => [...prev, ...newPhotos]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemovePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!businessId || !bookingDetailsId || !serviceId || !targetUserId) {
      toast({
        title: "Missing booking information",
        description: "Required booking details are missing from the URL.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    if (rating === 0) {
      toast({
        title: "Rating required",
        description: "Please select a star rating before submitting.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("business_id", businessId);
      formData.append("booking_details_id", bookingDetailsId);
      formData.append("service_id", serviceId);
      formData.append("target_user_id", targetUserId);
      formData.append("rating_text", feedback);
      formData.append("rating_number", rating.toString());
      formData.append("post_by", "customer");
      formData.append("isAnonymouslyPosted", isAnonymous.toString());

      photos.forEach((photo, index) => {
        formData.append(`photo_${index}`, photo);
      });

      const res = await reviewRating(formData);

      if (res?.status === 200) {
        setSubmitted(true);
        toast({
          title: "Review Submitted",
          description: res.message || "Thank you for your feedback!",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } else {
        toast({
          title: "An error occurred",
          description:
            res?.message || "Unable to submit review. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch {
      toast({
        title: "An error occurred",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }

    setLoading(false);
  };

  if (!jwt) return null;

  return (
    <Box
      display="flex"
      flexDir="column"
      minH="100vh"
      bg="linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 40%, #FFF7F3 70%, #D8F1F2 100%)"
    >
      <Flex
        boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
        minH="72px"
        px={{ base: "20px", md: "10%" }}
        align="center"
        justify="space-between"
        bg="white"
      >
        <Flex align="center" gap="16px">
          <Icon
            fontSize={24}
            color="#19202D"
            cursor="pointer"
            onClick={() => navigate("/")}
          >
            <IoIosArrowBack />
          </Icon>

          <Image
            src={LogoIcon}
            alt="Book-d Logo"
            h="32px"
            w="19px"
            cursor="pointer"
            onClick={() => navigate("/")}
          />
        </Flex>
      </Flex>

      {isMobile && bookingDetailsId && (
        <OpenInAppBanner
          deepLink={`bookd://booking-details/${bookingDetailsId}?openReviewSheet=true`}
          isIOS={isIOS}
        />
      )}

      <Flex flex={1} align="flex-start" justify="center" px="20px" py="40px">
        <Flex
          flexDir="column"
          align="center"
          maxW="580px"
          w="100%"
          bg="white"
          borderRadius="24px"
          boxShadow="0px 4px 20px rgba(0, 0, 0, 0.08)"
          p={{ base: "32px 24px", md: "48px 40px" }}
        >
          {loading ? (
            <Flex py="40px">
              <CircularProgress isIndeterminate color="#FF8E3C" size="60px" />
            </Flex>
          ) : submitted ? (
            <>
              <Flex
                w="80px"
                h="80px"
                borderRadius="full"
                bg="#DBF1F2"
                align="center"
                justify="center"
                mb="24px"
              >
                <FaRegCircleCheck size={44} color="#267684" />
              </Flex>

              <Heading
                fontSize={{ base: "24px", md: "28px" }}
                color="#19202D"
                fontFamily="RedHatDisplay-Bold"
                textAlign="center"
              >
                Review Submitted
              </Heading>

              <Box
                h="3px"
                w="60px"
                borderRadius="10px"
                bg="#FF8E3C"
                my="20px"
              />

              <Text
                fontSize={{ base: "15px", md: "16px" }}
                color="#4F4F51"
                lineHeight="26px"
                textAlign="center"
              >
                Thank you for your feedback! Your review helps us improve our
                services and helps other customers make informed decisions.
              </Text>

              <Button
                mt="32px"
                bg="#FF8E3C"
                _hover={{ bg: "#e07a30" }}
                borderRadius="30px"
                color="white"
                h="52px"
                px="40px"
                fontSize="16px"
                fontFamily="RedHatDisplay-Bold"
                onClick={() => navigate("/")}
              >
                Back to Home
              </Button>
            </>
          ) : (
            <>
              <Flex
                w="80px"
                h="80px"
                borderRadius="full"
                bg="#FFF0E5"
                align="center"
                justify="center"
                mb="24px"
              >
                <Text fontSize="36px" lineHeight={1}>
                  ⭐
                </Text>
              </Flex>

              <Heading
                fontSize={{ base: "24px", md: "28px" }}
                color="#19202D"
                fontFamily="RedHatDisplay-Bold"
                textAlign="center"
              >
                Rate Your Experience
              </Heading>

              <Box
                h="3px"
                w="60px"
                borderRadius="10px"
                bg="#FF8E3C"
                my="20px"
              />

              <Box w="100%" mb="24px">
                <Text
                  fontSize="15px"
                  color="#4F4F51"
                  textAlign="center"
                  mb="16px"
                >
                  How was your visit? Tap the stars to rate.
                </Text>

                <Flex justify="center" gap="8px">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Box
                      key={star}
                      cursor="pointer"
                      onClick={() => handleStarClick(star)}
                      transition="transform 0.15s"
                      _hover={{ transform: "scale(1.1)" }}
                    >
                      {star <= rating ? (
                        <FaStar size={36} color="#FF8E3C" />
                      ) : (
                        <FaRegStar size={36} color="#9BA3A3" />
                      )}
                    </Box>
                  ))}
                </Flex>

                {rating > 0 && (
                  <Text
                    fontSize="14px"
                    color="#267684"
                    textAlign="center"
                    mt="12px"
                    fontWeight={600}
                  >
                    {rating === 1 && "Poor"}
                    {rating === 2 && "Fair"}
                    {rating === 3 && "Good"}
                    {rating === 4 && "Very Good"}
                    {rating === 5 && "Excellent"}
                  </Text>
                )}
              </Box>

              <Box w="100%" mb="24px">
                <Heading
                  fontSize="16px"
                  color="#19202D"
                  mb="8px"
                  fontFamily="RedHatDisplay-Bold"
                >
                  Share your feedback
                </Heading>

                <Textarea
                  placeholder="Tell us about your experience... What did you like? What could we improve?"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  borderRadius="16px"
                  border="1px solid #E9EDEF"
                  fontSize="15px"
                  color="#19202D"
                  _placeholder={{ color: "#9BA3A3" }}
                  _focusVisible={{ borderColor: "#FF8E3C" }}
                  minH="120px"
                  resize="vertical"
                  p="16px"
                />
              </Box>

              <Box w="100%" mb="24px">
                <Heading
                  fontSize="16px"
                  color="#19202D"
                  mb="12px"
                  fontFamily="RedHatDisplay-Bold"
                >
                  Add Photos (Optional)
                </Heading>

                <Flex gap="12px" flexWrap="wrap">
                  {photos.map((photo, index) => (
                    <Box
                      key={index}
                      position="relative"
                      w="80px"
                      h="80px"
                      borderRadius="12px"
                      overflow="hidden"
                      border="1px solid #E9EDEF"
                    >
                      <Image
                        src={URL.createObjectURL(photo)}
                        alt={`Photo ${index + 1}`}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                      />
                      <Box
                        position="absolute"
                        top="4px"
                        right="4px"
                        w="24px"
                        h="24px"
                        bg="rgba(0, 0, 0, 0.5)"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        onClick={() => handleRemovePhoto(index)}
                      >
                        <IoMdClose size={16} color="white" />
                      </Box>
                    </Box>
                  ))}

                  {photos.length < 4 && (
                    <Box
                      w="80px"
                      h="80px"
                      borderRadius="12px"
                      border="2px dashed #E9EDEF"
                      display="flex"
                      flexDir="column"
                      alignItems="center"
                      justifyContent="center"
                      cursor="pointer"
                      _hover={{ borderColor: "#FF8E3C", bg: "#FFF7F3" }}
                      transition="all 0.2s"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <IoCameraOutline size={28} color="#9BA3A3" />
                      <Text fontSize="11px" color="#9BA3A3" mt="4px">
                        Add Photo
                      </Text>
                    </Box>
                  )}
                </Flex>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handlePhotoSelect}
                />

                <Text fontSize="12px" color="#9BA3A3" mt="8px">
                  You can add up to 4 photos
                </Text>
              </Box>

              <Checkbox
                isChecked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                colorScheme="orange"
                mb="24px"
                w="100%"
              >
                <Text fontSize="14px" color="#4F4F51">
                  Submit anonymously
                </Text>
              </Checkbox>

              <Button
                bg="#FF8E3C"
                _hover={{ bg: "#e07a30" }}
                borderRadius="30px"
                color="white"
                h="58px"
                w={{ base: "100%", md: "280px" }}
                fontSize="17px"
                fontFamily="RedHatDisplay-Bold"
                onClick={handleSubmit}
                isLoading={loading}
                loadingText="Submitting..."
                disabled={loading}
              >
                Submit Review
              </Button>
            </>
          )}

          {!loading && (
            <Flex align="center" gap="6px" mt="40px" opacity={0.5}>
              <Text fontSize="13px" color="#4F4F51">
                Powered by
              </Text>
              <Image src={LogoText} h="18px" objectFit="contain" />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};
